import gql from 'graphql-tag';
import { useQuery } from 'urql';

import { reviewDocumentForm_document } from '@/forms/review-document-form/ReviewDocumentForm';
import { useQueryParams } from '@/hooks';
import { mapVariables } from '@/utils';
import { makeOrderBy, makePagination } from '@/utils/pagination';
import {
  GlobalPlayerDocumentsQuery,
  GlobalPlayerDocumentsQueryVariables,
} from './__generated__/useGlobalPlayerDocuments';
import { queryParams } from './queryParams';

export const globalPlayerDocuments_document = gql`
  fragment GlobalPlayerDocuments_document on Document {
    player {
      id
      uuid
      brand {
        name
      }
    }
    documentType
    expirationDate
    id
    status
    uuid
    verificationTypes
    uploadedAt
    uploadedBy {
      ... on PlayerInitiator {
        player {
          id
          firstName
          lastName
          email
        }
      }
      ... on AgentInitiator {
        agent {
          firstName
          lastName
          email
        }
      }
    }
    verifiedAt
    verifiedBy {
      ... on PlayerInitiator {
        player {
          firstName
          lastName
          email
        }
      }
      ... on AgentInitiator {
        agent {
          firstName
          lastName
          email
        }
      }
    }
    deletedAt
    deletedBy {
      ... on PlayerInitiator {
        player {
          firstName
          lastName
          email
        }
      }
      ... on AgentInitiator {
        agent {
          firstName
          lastName
          email
        }
      }
    }
    ...ReviewDocumentForm_document
  }
  ${reviewDocumentForm_document}
`;

export const GLOBAL_PLAYER_DOCUMENTS_QUERY = gql`
  query GlobalPlayerDocuments(
    $playerGlobalId: ID!
    $after: String
    $before: String
    $first: Int
    $last: Int
    $orderBy: DocumentSearchOrder
    $desc: Boolean
  ) {
    playerGlobal(playerGlobalId: $playerGlobalId) {
      id
      globalDocuments(
        after: $after
        before: $before
        first: $first
        last: $last
        orderBy: $orderBy
        desc: $desc
      ) {
        edges {
          node {
            document {
              ...GlobalPlayerDocuments_document
            }
          }
        }
        pageInfo {
          endCursor
          hasNextPage
          startCursor
          hasPreviousPage
        }
      }
    }
  }
  ${globalPlayerDocuments_document}
`;

export default function usePlayerDocuments(playerGlobalId: string) {
  const [query, setQuery] = useQueryParams(queryParams);
  const [{ data, fetching }, refresh] = useQuery<
    GlobalPlayerDocumentsQuery,
    GlobalPlayerDocumentsQueryVariables
  >({
    query: GLOBAL_PLAYER_DOCUMENTS_QUERY,
    variables: {
      playerGlobalId,
      ...mapVariables(query),
    },
  });

  const documents = data?.playerGlobal.globalDocuments;

  return {
    refresh,
    fetching,
    defaultFilter: query,
    updateFilter: (values: {}) => setQuery(values, 'replaceIn'),
    ...makeOrderBy(query, setQuery),
    ...makePagination(query, setQuery, documents?.pageInfo),
    globalPlayerDocuments: documents?.edges?.map((edge) => edge?.node),
  };
}
